import { Select as RadixSelect } from 'radix-ui';
import { forwardRef, useId, type ReactNode } from 'react';

import { cx } from '../../lib/cx';
import { Icon } from '../Icon/Icon';
import styles from './select.module.css';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps {
  label: string;
  /**
   * Спрятать подпись ВИЗУАЛЬНО, оставив её скринридеру.
   *
   * Не `label=""`: поле без имени безымянно и для скринридера тоже. Нужно там, где
   * подпись очевидна из содержимого (список точек над списком точек) и стоит строки
   * текста, ничего не добавляя.
   */
  hideLabel?: boolean;
  options: readonly SelectOption[];
  /** Текущее значение. Пустая строка — «не выбрано»: показывается `placeholder`. */
  value: string;
  onValueChange: (value: string) => void;
  /**
   * Пункт «ничего» («Все статусы», «Не выбран»). Выбирается, как любой другой,
   * и даёт пустую строку — так фильтр сбрасывается тем же списком, которым задан.
   */
  placeholder?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  /** Имя для отправки формы. */
  name?: string;
  /** Дополнение справа от поля — например, чип выбранной категории. */
  adornment?: ReactNode;
  className?: string;
}

// Radix не допускает пустую строку значением пункта: она зарезервирована под «сброс».
// Пункт-заглушку кодируем служебным значением и переводим обратно в `''` на выходе.
const EMPTY = '__empty__';

/**
 * Выпадающий список на Radix Select (решение владельца 2026-09-21).
 *
 * Раньше поле было нативным `<select>`: мы оформляли закрытое поле, а раскрытый список
 * рисовала ОС — со своим шрифтом, своими отступами и цветом выделения из системных
 * настроек (у владельца он оказался красным, то есть цветом «просрочено»). Теперь
 * список наш целиком: шрифт, плотность и цвета «Табло», одинаковые на Mac, Windows
 * и Android. Пункты — не ниже `--ds-control-min`, чтобы на кухонном планшете
 * попадать пальцем так же уверенно, как в системный список.
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    label,
    hideLabel,
    options,
    value,
    onValueChange,
    placeholder,
    hint,
    error,
    required,
    disabled,
    name,
    adornment,
    className,
  },
  ref,
) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  let describedBy: string | undefined;
  if (error) {
    describedBy = errorId;
  } else if (hint) {
    describedBy = hintId;
  }

  const current = value === '' && placeholder ? EMPTY : value;

  return (
    <div className={cx(styles.field, className)}>
      {/* Звёздочка стоит РЯДОМ с label, а не внутри него: внутри она попадает
          в текст метки, и метка перестаёт совпадать с видимым названием поля —
          для скринридера, для автотестов и для любого поиска по названию. */}
      <span className={cx(styles.labelRow, hideLabel ? styles.visuallyHidden : undefined)}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {required ? (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        ) : null}
      </span>

      <div className={styles.control}>
        <RadixSelect.Root
          value={current === '' ? undefined : current}
          onValueChange={(next) => onValueChange(next === EMPTY ? '' : next)}
          disabled={disabled}
          required={required}
          name={name}
        >
          <RadixSelect.Trigger
            ref={ref}
            id={id}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={cx(styles.trigger, error ? styles.invalid : undefined)}
          >
            <span className={styles.value}>
              <RadixSelect.Value placeholder={placeholder ?? ''} />
            </span>
            <RadixSelect.Icon className={styles.chevron}>
              <Icon name="chevronDown" size={16} />
            </RadixSelect.Icon>
          </RadixSelect.Trigger>

          <RadixSelect.Portal>
            <RadixSelect.Content
              className={styles.content}
              position="popper"
              sideOffset={4}
              collisionPadding={8}
            >
              <RadixSelect.Viewport className={styles.viewport}>
                {placeholder ? <Item value={EMPTY} label={placeholder} muted /> : null}
                {options.map((option) => (
                  <Item
                    key={option.value}
                    value={option.value}
                    label={option.label}
                    disabled={option.disabled}
                  />
                ))}
              </RadixSelect.Viewport>
            </RadixSelect.Content>
          </RadixSelect.Portal>
        </RadixSelect.Root>
        {adornment ? <span className={styles.adornment}>{adornment}</span> : null}
      </div>

      {error ? (
        <span id={errorId} className={styles.error} role="alert">
          {error}
        </span>
      ) : null}
      {!error && hint ? (
        <span id={hintId} className={styles.hint}>
          {hint}
        </span>
      ) : null}
    </div>
  );
});

function Item({
  value,
  label,
  disabled,
  muted,
}: {
  value: string;
  label: string;
  disabled?: boolean;
  muted?: boolean;
}) {
  return (
    <RadixSelect.Item
      value={value}
      disabled={disabled}
      className={cx(styles.item, muted ? styles.itemMuted : undefined)}
    >
      <RadixSelect.ItemText>{label}</RadixSelect.ItemText>
      <RadixSelect.ItemIndicator className={styles.indicator}>
        <Icon name="check" size={16} />
      </RadixSelect.ItemIndicator>
    </RadixSelect.Item>
  );
}
